import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import ButtonSave from "@View/components/buttons/ButtonSave";
import GetAppIcon from "@material-ui/icons/GetApp";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import DefaultRangeSlider from "@View/components/formInputs/DefaultRangeSlider";
import ModalLayout from "@View/components/ModalLayout";
import getAPI from "@API/index";
import { downloadFileByPath } from "@Dao/api";

const useDocStyles = makeStyles(() => ({
  wrapper: {
    marginTop: "-6px",
    marginBottom: "20px",
    position: "relative",
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "6px",
  },
  label: {
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#A1ADBA",
  },
  uploadBtn: {
    padding: "2px",
    color: "#6FA4F2",
  },
  container: {
    border: "1px solid #E1E6F1",
    borderRadius: "8px",
    padding: "10px 15px",
    minHeight: "48px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  emptyText: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#A1ADBA",
  },
  chip: {
    width: "100%",
    justifyContent: "flex-start",
    fontSize: "13px",
    fontWeight: 500,
    color: "#54606F",
    backgroundColor: "#F3F5F7",
    "& .MuiChip-label": {
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiChip-deleteIcon": {
      marginLeft: "auto",
      color: "#A1ADBA",
      "&:hover": { color: "#54606F" },
    },
  },
  uploadError: {
    fontSize: "12px",
    color: "rgba(164, 36, 28, 1)",
    marginTop: "6px",
  },
}));

const MAX_FILES = 10;
const MAX_SIZE_MB = 5;
const ACCEPTED_FORMATS = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png"];

const LeadDocumentsSection = ({ leadId }) => {
  const classes = useDocStyles();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    getAPI()
      .request({ url: `/api/lead/${leadId}/documents`, suppressError: true })
      .then((res) => setDocs(res?.data ?? []))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [leadId]);

  const handleDownload = (doc) => {
    getAPI()
      .request({ url: `/api/lead/${leadId}/documents/${doc.id}/url`, suppressError: true })
      .then((res) => {
        const url = res?.data ?? res;
        if (url) downloadFileByPath(url, doc.originalFileName);
      })
      .catch(() => { });
  };

  const handleDelete = (doc) => {
    setDeletingId(doc.id);
    getAPI()
      .request({ method: "delete", url: `/api/lead/${leadId}/documents/${doc.id}`, suppressError: true })
      .then(() => setDocs((prev) => prev.filter((d) => d.id !== doc.id)))
      .catch(() => { })
      .finally(() => setDeletingId(null));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploadError("");

    const oversized = files.filter((f) => f.size > MAX_SIZE_MB * 1024 * 1024);
    if (oversized.length) {
      setUploadError(`Файл${oversized.length > 1 ? "ы" : ""} превышают ${MAX_SIZE_MB} МБ: ${oversized.map((f) => f.name).join(", ")}`);
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    const slotsLeft = MAX_FILES - docs.length;
    const filesToUpload = files.slice(0, slotsLeft);
    if (filesToUpload.length < files.length) {
      setUploadError(`Будет загружено ${filesToUpload.length} из ${files.length} — максимум ${MAX_FILES}`);
    }
    if (!filesToUpload.length) {
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    setUploading(true);
    (async () => {
      const added = [];
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await getAPI()
          .request({ method: "post", url: `/api/lead/${leadId}/documents`, data: formData, suppressError: true })
          .catch(() => null);
        if (res?.data) added.push(res.data);
        else break;
      }
      if (added.length) setDocs((prev) => [...prev, ...added]);
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    })();
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.labelRow}>
        <span className={classes.label}>{t("inputs.Documents")}</span>
        <Tooltip title={`PDF, DOC, DOCX, XLS, XLSX, JPG, PNG · макс. ${MAX_SIZE_MB} МБ`} placement="top">
          <span>
            <IconButton
              size="small"
              className={classes.uploadBtn}
              onClick={() => { setUploadError(""); fileInputRef.current?.click(); }}
              disabled={uploading || docs.length >= MAX_FILES}
            >
              {uploading ? <CircularProgress size={16} /> : <AttachFileIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_FORMATS.map((f) => `.${f}`).join(",")}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <div className={classes.container}>
        {loading ? (
          <CircularProgress size={18} />
        ) : docs.length === 0 ? (
          <span className={classes.emptyText}>{t("documents.noDocuments", "Нет документов")}</span>
        ) : (
          docs.map((doc) => (
            <Tooltip key={doc.id} title={doc.originalFileName} placement="top">
              <Chip
                label={doc.originalFileName}
                size="small"
                className={classes.chip}
                onDelete={deletingId === doc.id ? undefined : () => handleDelete(doc)}
                deleteIcon={
                  deletingId === doc.id
                    ? <CircularProgress size={14} />
                    : undefined
                }
                onClick={() => handleDownload(doc)}
                icon={<GetAppIcon style={{ fontSize: 14 }} />}
              />
            </Tooltip>
          ))
        )}
      </div>

      {uploadError && <div className={classes.uploadError}>{uploadError}</div>}
    </div>
  );
};

const EditLeadModal = ({ lead, open, handleClose, onSaved }) => {
  const [form, setForm] = useState({
    requestedAmount: "",
    requestedTermMonths: "",
    creditProduct: "",
    borrowerEmail: "",
    responsibleEmployeePhone: "",
    ceoPhone: "",
  });

  useEffect(() => {
    if (lead) {
      setForm({
        requestedAmount: lead.requestedAmount != null ? String(lead.requestedAmount) : "",
        requestedTermMonths: lead.requestedTermMonths != null ? String(lead.requestedTermMonths) : "",
        creditProduct: lead.creditProduct || "",
        borrowerEmail: lead.borrowerEmail || "",
        responsibleEmployeePhone: lead.responsibleEmployeePhone || "",
        ceoPhone: lead.ceoPhone || "",
      });
    }
  }, [lead]);

  if (!lead) return null;

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = (e) => {
    e.preventDefault();

    const payload = {
      id: lead.id,
      inn: lead.inn,
      borrowerName: lead.borrowerName,
      borrowerEmail: form.borrowerEmail,
      responsibleEmployeeName: lead.responsibleEmployeeName,
      responsibleEmployeePhone: form.responsibleEmployeePhone,
      ceoPhone: form.ceoPhone,
      creditProduct: form.creditProduct,
      requestedAmount: form.requestedAmount !== "" ? Number(form.requestedAmount) : null,
      requestedTermMonths: form.requestedTermMonths !== "" ? Number(form.requestedTermMonths) : null,
      stopFactors: lead.stopFactors,
      stopFactorsColor: lead.stopFactorsColor,
      documents: lead.documents,
      additionalPropertiesJson: lead.additionalPropertiesJson,
    };

    getAPI()
      .request({
        method: "put",
        url: `/api/lead/${lead.id}`,
        data: payload,
        successText: "Изменения сохранены",
        errorText: "Не удалось сохранить. Попробуйте ещё раз.",
      })
      .then(() => {
        handleClose();
        onSaved({ ...lead, ...payload });
      })
      .catch(() => { });
  };

  const footer = <ButtonSave marginTop="0">buttons.Save</ButtonSave>;

  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title="h1.EditLead"
      subtitle={lead.borrowerName}
      onSubmit={handleSave}
      footer={footer}
    >
      <DefaultInput
          label="inputs.RequestedAmount"
          value={form.requestedAmount}
          onChange={setField("requestedAmount")}
          type="number"
        />
        <DefaultRangeSlider
          label="inputs.TermMonths"
          value={form.requestedTermMonths === "" ? 12 : Number(form.requestedTermMonths)}
          onChange={(v) => setField("requestedTermMonths")(String(v))}
          min={1}
          max={60}
        />
        <DefaultInput
          label="inputs.CreditProduct"
          value={form.creditProduct}
          onChange={setField("creditProduct")}
        />
        <DefaultInput
          label="inputs.BorrowerEmail"
          value={form.borrowerEmail}
          onChange={setField("borrowerEmail")}
        />
        <DefaultInput
          label="inputs.ResponsibleEmployeePhone"
          value={form.responsibleEmployeePhone}
          onChange={setField("responsibleEmployeePhone")}
        />
        <DefaultInput
          label="inputs.CeoPhone"
          value={form.ceoPhone}
          onChange={setField("ceoPhone")}
        />
        <LeadDocumentsSection leadId={lead.id} />
    </ModalLayout>
  );
};

export default EditLeadModal;
